import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { doc, getDoc, collection, getDocs, writeBatch, increment, serverTimestamp, setDoc, deleteDoc, query, where, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { FiCalendar, FiMapPin, FiUser, FiArrowLeft, FiX, FiCheck, FiTrash2, FiUserCheck, FiChevronRight, FiMinus, FiPlus, FiAlertTriangle, FiEdit2, FiShare2 } from 'react-icons/fi';

const AGE_ORDER = {
    'mini': 1, 'gyerek': 2, 'junior': 3, 'kadet': 4, 'ifi': 5,
    'fiatal_felnott': 6, 'felnott': 7, 'szenior': 8, 'veteran': 9
};

// --- STÍLUSOK ---
const pageContainerStyle = {
    padding: '2rem',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#f0f2f5',
    minHeight: 'calc(100vh - 4rem)',
    maxWidth: '800px',
    margin: '0 auto'
};

const cardStyle = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '2rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    position: 'relative'
};

const titleStyle = {
    color: '#b30000',
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    paddingRight: '120px' // Hely az ikonnak
};

const modalCloseButtonStyle = {
    cursor: 'pointer',
    backgroundColor: '#f2f2f2',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none'
};

const modalOverlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center',
    alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(3px)'
};

const modalContentStyle = (isMobile) => ({
    backgroundColor: '#fff', width: '90%', maxWidth: '450px', borderRadius: '15px',
    boxShadow: '0 15px 35px rgba(0,0,0,0.3)', overflow: 'hidden', textAlign: 'center',
    maxHeight: isMobile ? '75vh' : '80vh',
    marginBottom: isMobile ? '0' : '40px',
    display: 'flex', flexDirection: 'column'
});

const modalHeaderStyle = (isMobile) => ({
    backgroundColor: '#b30000', color: '#fff', padding: isMobile ? '8px 15px' : '15px 20px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexShrink: 0
});

const modalBodyStyle = (isMobile) => ({ padding: isMobile ? '15px' : '25px', overflowY: 'auto', flex: 1, textAlign: 'center' });

const modalFooterStyle = (isMobile) => ({
    padding: isMobile ? '10px 15px' : '18px 25px 24px 25px',
    backgroundColor: '#f9f9f9',
    borderTop: '1px solid #eee',
    flexShrink: 0
});

const withdrawButtonStyle = {
    width: '100%', backgroundColor: '#b30000', color: '#fff', border: 'none',
    padding: '12px', borderRadius: '10px', fontWeight: 'bold', fontSize: '1rem',
    cursor: 'pointer', marginTop: '15px'
};

const cancelButtonStyle = {
    width: '100%', backgroundColor: '#eee', color: '#333', border: 'none',
    padding: '12px', borderRadius: '10px', fontWeight: 'bold', fontSize: '1rem',
    cursor: 'pointer', marginTop: '10px'
};

const detailStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#555',
    marginBottom: '0.5rem',
    fontSize: '1rem'
};

const tabContainerStyle = {
    display: 'flex',
    gap: '20px',
    borderBottom: '2px solid #eee',
    marginBottom: '1.5rem',
    marginTop: '2rem'
};

const tabButtonStyle = (isActive) => ({
    padding: '10px 20px',
    border: 'none',
    background: 'none',
    fontSize: '1rem',
    fontWeight: 'bold',
    color: isActive ? '#b30000' : '#666',
    borderBottom: isActive ? '3px solid #b30000' : '3px solid transparent',
    cursor: 'pointer',
    marginBottom: '-2px',
    transition: 'all 0.2s'
});

const descriptionStyle = {
    lineHeight: '1.6',
    color: '#444',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    textAlign: 'left'
};

const participantHeaderStyle = {
    backgroundColor: '#fff5f5',
    padding: '8px 15px',
    borderRadius: '8px',
    color: '#b30000',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    marginTop: '1.5rem',
    marginBottom: '0.5rem',
    borderLeft: '4px solid #b30000'
};

const participantRowStyle = {
    padding: '10px 15px',
    borderBottom: '1px solid #eee',
    display: 'flex',
    flexDirection: 'column'
};

const backButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    color: '#666',
    cursor: 'pointer',
    marginBottom: '1rem',
    fontSize: '1rem',
    padding: 0
};

const profileRegCardStyle = {
    backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '10px',
    border: '1px solid #eee', marginBottom: '15px'
};

const genderToggleStyle = {
    backgroundColor: '#b30000', color: '#fff', border: 'none', padding: '6px 12px',
    borderRadius: '8px', fontWeight: 'bold', fontSize: '0.85rem', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '5px'
};

const inputGroupStyle = { marginBottom: '15px', display: 'flex', flexDirection: 'column', gap: '5px' };

const regLabelStyle = { fontSize: '0.75rem', fontWeight: 'bold', color: '#b30000', textTransform: 'uppercase' };

const regSelectStyle = {
    padding: '10px', borderRadius: '8px', border: '1px solid #ddd',
    backgroundColor: '#fff', fontSize: '0.9rem'
};

const regTextAreaStyle = {
    ...regSelectStyle, minHeight: '80px', resize: 'vertical'
};

const lunchCounterStyle = {
    display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#f8f9fa',
    padding: '5px', borderRadius: '8px', border: '1px solid #ddd', width: 'fit-content'
};

const lunchBtnStyle = {
    backgroundColor: '#fff', color: '#b30000', border: '1px solid #ddd',
    width: '32px', height: '32px', borderRadius: '6px', cursor: 'pointer',
    display: 'flex', justifyContent: 'center', alignItems: 'center'
};

const regSubmitButtonStyle = {
    width: '100%', backgroundColor: '#b30000', color: '#fff', border: 'none',
    padding: '14px', borderRadius: '10px', fontWeight: 'bold', fontSize: '1rem',
    cursor: 'pointer', marginTop: '10px', boxShadow: '0 4px 6px rgba(179,0,0,0.2)'
};

// Fordítási táblázat
const translationMap = {
    'mini': 'Mini', 'gyerek': 'Gyerek', 'junior': 'Junior', 'kadet': 'Kadét',
    'fiatal_felnott': 'Fiatal felnőtt', 'felnott': 'Felnőtt', 'szenior': 'Szenior', 'veteran': 'Veterán',
    '3d_hdh_bb': 'BB (Barebow)', '3d_hdh_bg': 'BG (Fúvócső)', '3d_hdh_cb': 'CB (Csigás Barebow)',
    '3d_hdh_crb': 'CRB (Számszeríj)', '3d_hdh_cu': 'CU (Scope-os Csigás)',
    '3d_hdh_hu': 'HU (Vadász Csigás)', '3d_hdh_ol': 'OL (Olimpiai)',
    '3d_hdh_pbhb': 'PBHB (Tradi)', '3d_hdh_trlb': 'TRLB (Longbow)',
    '3d_hdh_trrb': 'TRRB (Vadászreflex)',
    'terep_olimpiai': 'OL (Olimpiai)', 'terep_csigas': 'CU (Scope-os Csigás)',
    'terep_barebow': 'BB (Barebow)', 'terep_cb': 'CB (Csigás Barebow)',
    'terep_hu': 'HU (Vadász Csigás)', 'terep_pbhb': 'PBHB (Tradi)',
    'terep_trlb': 'TRLB (Longbow)', 'terep_trrb': 'TRRB (Vadászreflex)',
    'palya_wa_ol': 'R (Olimpiai reflexíj)', 'palya_wa_cu': 'CU (Scope-os Csigás)',
    'palya_wa_hu': 'HU (Vadász Csigás)', 'palya_wa_bb': 'B (Barebow)',
    'palya_wa_trrb': 'TRRB (Vadászreflex)', 'palya_wa_trlb': 'TRLB (Longbow)',
    'palya_wa_pbhb': 'PBHB (Tradi)', 'palya_wa_crb': 'CRB (Számszeríj)',
    'tortenelmi_pusztai_modern': 'Modern pusztai íj',
    'tortenelmi_pusztai_termeszetes': 'Természetes pusztai íj',
    'tortenelmi_botij': 'Történelmi hosszúíj / Botíj',
    'tortenelmi_vadaszij': 'Vadászreflex (karbon és fa együtt)',
    'tortenelmi_vadaszij_fa': 'Vadászíj fa vesszővel',
    'tortenelmi_vadaszij_karbon': 'Vadászíj karbonvesszővel',
    'tortenelmi_ij': 'Történelmi íj',
    'tortenelmi_szamszerij': 'Történelmi számszeríj',
    'tortenelmi_bb': 'BB (Barebow)',
    'orom_wa_ol': 'OL (Olimpiai reflexíj)', 'orom_wa_cu': 'CU (Scope-os Csigás)',
    'orom_wa_hu': 'HU (Vadász Csigás)', 'orom_wa_cb': 'CB (Csigás Barebow)',
    'orom_wa_bb': 'BB (Barebow)', 'orom_wa_trrb': 'TRRB (Vadászreflex)',
    'orom_wa_trlb': 'TRLB (Longbow)', 'orom_wa_pbhb': 'PBHB (Pusztai íj)',
    'orom_vadaszij': 'Vadászreflex (karbon és fa együtt)',
    'orom_vadaszij_fa': 'Vadászíj fa vesszővel',
    'orom_vadaszij_karbon': 'Vadászíj karbonvesszővel',
    'orom_tortenelmi_ij': 'Történelmi íj',
    'orom_wa_crb': 'CRB (Számszeríj)',
 'orom_wa_bg': 'BG (Fúvócső)',
    'egyeb_wa_ol': 'OL (Olimpiai reflexíj)', 'egyeb_wa_cu': 'CU (Scope-os Csigás)',
    'egyeb_wa_hu': 'HU (Vadász Csigás)', 'egyeb_wa_cb': 'CB (Csigás Barebow)',
    'egyeb_wa_bb': 'BB (Barebow)', 'egyeb_wa_trrb': 'TRRB (Vadászreflex)',
    'egyeb_wa_trlb': 'TRLB (Longbow)', 'egyeb_wa_pbhb': 'PBHB (Pusztai íj)',
    'egyeb_wa_crb': 'CRB (Számszeríj)', 'egyeb_wa_bg': 'BG (Fúvócső)',
    'egyeb_altalanos': 'Egyéb Általános'
};

// --- ÚJ: KLUB NÉV FORMÁZÓ SEGÉDFÜGGVÉNY ---
const formatClubName = (name) => {
  if (!name || name === "Egyéb" || name === "N/A" || name === "Ismeretlen" || name === "Nincs megadva") return name;
  const specialCases = {
      "3d": "3D", "kft.": "Kft.", "se": "SE", "sk": "SK", "dse": "DSE",
      "kse": "KSE", "mavir": "MAVIR", "ifaa": "IFAA", "hdh-iaa": "HDH-IAA"
  };

  return name.split(' ').map(word => {
    const cleanWord = word.replace(/[^a-zA-ZáéíóöőúüűÁÉÍÓÖŐÚÜŰ0-9]/g, '').toLowerCase();
    if (specialCases[cleanWord]) return specialCases[cleanWord];
    if (word.startsWith('"')) {
        return '"' + (word.charAt(1)?.toUpperCase() || "") + word.slice(2).toLowerCase();
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(' ');
};

const getCompetitionTypeDisplay = (rawType) => {
    const type = rawType?.toLowerCase() || '';
    if (type.includes('3d')) return { label: '3D', color: '#28a745' };
    if (type.includes('palya') || type.includes('pálya')) return { label: 'Pálya', color: '#007bff' };
    if (type.includes('orom') || type.includes('öröm')) return { label: 'Örömijász', color: '#ffc107' };
    if (type.includes('tortenelmi') || type.includes('történelmi')) return { label: 'Történelmi', color: '#dc3545' };
    if (type.includes('terep')) return { label: 'Terep', color: '#6d4c41' };
    if (type.includes('egyeb') || type.includes('egyéb')) return { label: 'Egyéb', color: '#9c27b0' };
    return { label: rawType || 'Egyéb', color: '#9c27b0' };
};

// --- ÚJ: REGISZTRÁCIÓ LEZÁRTSÁG ELLENŐRZŐ SEGÉDFÜGGVÉNY ---
const checkIsRegistrationClosed = (comp) => {
    if (!comp) return false;

    // 1. Kézi lezárás a versenyirodában
    if (comp.registrationClosed === true) return true;

    // 2. Lezárt státusz
    if (comp.status === 'Lezárt' || comp.status === 'Ready to Upload') return true;

    // 3. Határidő lejárta
    if (comp.registrationDeadline) {
        try {
            const deadlineStr = comp.registrationDeadline;
            let deadlineDate;
            if (deadlineStr.includes('T')) {
                deadlineDate = new Date(deadlineStr);
            } else {
                deadlineDate = new Date(deadlineStr);
                deadlineDate.setHours(23, 59, 59, 999);
            }
            if (Date.now() > deadlineDate.getTime()) {
                return true;
            }
        } catch (e) {
            console.error("Hiba a határidő ellenőrzésekor:", e);
        }
    }

    return false;
};

const FullViewCompetition = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [competition, setCompetition] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingParticipants, setLoadingParticipants] = useState(false);
    const [activeTab, setActiveTab] = useState('description');
    const [selectedEvent, setSelectedEvent] = useState(null); // ÚJ
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null); // ÚJ
    const [existingRegistration, setExistingRegistration] = useState(null); // ÚJ: A már elmentett nevezési adatok
    const [isJoined, setIsJoined] = useState(false);
    const [isClubAccount, setIsClubAccount] = useState(false); // ÚJ: Egyesületi fiók ellenőrzése
    const [isCheckingUserType, setIsCheckingUserType] = useState(true); // ÚJ: Villogás elleni védelem


    // --- NEVEZÉSI MODAL ÁLLAPOTOK ---
    const [showOptionsModal, setShowOptionsModal] = useState(false); // ÚJ
    const [showRegModal, setShowRegModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [regData, setRegData] = useState({
        gender: 'Férfi',
        bowCategory: '',
        ageCategory: '',
        lunchCount: 0,
        lunchSelections: { A: 0, B: 0, C: 0 }, // ÚJ
        comment: ''
    });
    const [additionalBowCategories, setAdditionalBowCategories] = useState([]);

    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [showTargetsModal, setShowTargetsModal] = useState(false);
    const [showMapModal, setShowMapModal] = useState(false); // ÚJ
    const [showAuthModal, setShowAuthModal] = useState(false); // ÚJ
    const [isWithdrawing, setIsWithdrawing] = useState(false);
    const [withdrawSuccess, setWithdrawSuccess] = useState(false);

    // --- TÖBBES CSALÁDI NEVEZÉSI ÁLLAPOTOK ---
    const [activeCompetitorsList, setActiveCompetitorsList] = useState([]);
    const [currentCompetitorIndex, setCurrentCompetitorIndex] = useState(0);
    const [showSelectPeopleModal, setShowSelectPeopleModal] = useState(false);
    const [subProfiles, setSubProfiles] = useState([]);
    const [registeredFamilyForWithdraw, setRegisteredFamilyForWithdraw] = useState([]);
    const [selectedWithdrawUids, setSelectedWithdrawUids] = useState({});

    // --- ÚJ: VISSZA GOMB KEZELÉSE A MODALOKHOZ ---
    useEffect(() => {
        const handlePopState = (e) => {
            if (showMapModal) {
                setShowMapModal(false);
                // Megállítjuk a tényleges visszalépést
                window.history.pushState(null, null, window.location.pathname);
            }
        };

        if (showMapModal) {
            window.history.pushState(null, null, window.location.pathname);
            window.addEventListener('popstate', handlePopState);
        }

        return () => window.removeEventListener('popstate', handlePopState);
    }, [showMapModal]);

    // --- ÚJ: PÁLYATÉRKÉP LEJÁRATI ELLENŐRZÉS ---
    const isMapVisible = useMemo(() => {
        if (!competition?.courseMapPublished || !competition?.courseMapImageUrl || !competition?.courseMapPublishedAt) return false;

        try {
            const publishedAt = competition.courseMapPublishedAt.toDate
                ? competition.courseMapPublishedAt.toDate()
                : new Date(competition.courseMapPublishedAt);

            const thirtyDaysMillis = 30 * 24 * 60 * 60 * 1000;
            return Date.now() < (publishedAt.getTime() + thirtyDaysMillis);
        } catch (e) {
            return false;
        }
    }, [competition]);

    // --- ÚJ: KÉP ELŐTÖLTÉS A CACHE MIATT ---
    const mapUrlWithCacheBuster = useMemo(() => {
        if (!competition?.courseMapImageUrl) return "";
        return competition.courseMapImageUrl.includes('?')
            ? `${competition.courseMapImageUrl}&t=${Date.now()}`
            : `${competition.courseMapImageUrl}?t=${Date.now()}`;
    }, [competition?.courseMapImageUrl]);

    // --- AUTOMATIKUS APP-NYITÓ ÁTIRÁNYÍTÁS MESSENGER IN-APP BROWSER ESETÉN (ANDROID) ---
    useEffect(() => {
        if (!id) return;
        const ua = navigator.userAgent || '';
        const isAndroid = /Android/i.test(ua);
        const isMessengerOrFB = /FB_IAB|FBAN|FBAV|Messenger/i.test(ua);

        if (isAndroid && isMessengerOrFB) {
            const fallbackUrl = encodeURIComponent(window.location.href);
            const intentUrl = `intent://verseny/${id}#Intent;scheme=archerynexus;package=com.archerynexus.app;S.browser_fallback_url=${fallbackUrl};end;`;
            window.location.href = intentUrl;
        }
    }, [id]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);

        const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user && id) {
                // --- ÚJ: Ellenőrizzük, hogy egyesület-e ---
                setIsCheckingUserType(true);
                try {
                    const clubDocRef = doc(db, "Clubs", user.uid);
                    const clubDocSnap = await getDoc(clubDocRef);
                    if (clubDocSnap.exists()) {
                        setIsClubAccount(true);
                    } else {
                        setIsClubAccount(false);
                        // Profil adatok betöltése (Csak versenyzőknek)
                        const userDocRef = doc(db, "users", user.uid);
                        const userDocSnap = await getDoc(userDocRef);
                        if (userDocSnap.exists()) {
                            const profile = userDocSnap.data();
                            setUserProfile(profile);
                            setRegData(prev => ({ ...prev, gender: profile.gender || 'Férfi' }));
                        }
                    }
                } catch (err) { console.error("Hiba a fiók típusának ellenőrzésekor:", err); }

                try {
                    // Családi státusz ellenőrzése
                    const qFam = query(collection(db, "events", id, "participants"), where("registeredByUid", "==", user.uid));
                    const famSnap = await getDocs(qFam);
                    let joined = !famSnap.empty;

                    if (!joined) {
                        const partRef = doc(db, "events", id, "participants", user.uid);
                        const partSnap = await getDoc(partRef);
                        joined = partSnap.exists();
                        if (joined) {
                            setExistingRegistration(partSnap.data());
                        } else {
                            setExistingRegistration(null);
                        }
                    } else {
                        setExistingRegistration(famSnap.docs[0].data());
                    }
                    setIsJoined(joined);
                } catch (err) {
                    console.error("Hiba a nevezés ellenőrzésekor:", err);
                } finally {
                    setIsCheckingUserType(false);
                }
            } else {
                setIsJoined(false);
                setUserProfile(null);
                setExistingRegistration(null);
                setIsClubAccount(false);
                setIsCheckingUserType(false); // Kijelentkezve nem kell várni
            }
        });
        return () => {
            unsubscribeAuth();
            window.removeEventListener('resize', handleResize);
        };
    }, [id]);

    // --- ÚJ: AUTOMATA NEVEZÉSI ABLAK NYITÁS BEJELENTKEZÉS UTÁN ---
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('autoOpen') === 'true' && currentUser) {
            // Rövid késleltetés, hogy a profil adatok biztosan betöltődjenek
            const timer = setTimeout(() => {
                if (isJoined) {
                    setShowOptionsModal(true); // Ha már nevezett, a kezelő panelt nyitjuk
                } else {
                    setShowRegModal(true);
                }
                // URL letisztítása
                const newUrl = window.location.pathname;
                window.history.replaceState({}, '', newUrl);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [currentUser, isJoined]);

    const handleNavigateGps = () => {
        if (!competition) return;
        const lat = competition.gpsLatitude;
        const lng = competition.gpsLongitude;
        const hasGps = lat && lng && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng));
        const dest = hasGps ? `${lat},${lng}` : encodeURIComponent(competition.location || '');

        if (isMobile) {
            // Mobil böngészőből a `geo:` URI séma megnyitja a telefon natív GPS appját (HERE WeGo / Waze / Google Maps)!
            window.location.href = hasGps ? `geo:${lat},${lng}?q=${lat},${lng}` : `geo:0,0?q=${dest}`;
        } else {
            // PC-n új lapon megnyitja a Google Maps-et
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}`, '_blank');
        }
    };

    const handleShareCompetition = async () => {
        const shareUrl = window.location.href;
        const compName = competition?.competitionName || 'Verseny';
        const shareText = `Nézd meg ezt a versenyt az ArcheryNexus-on: ${compName}!\n${shareUrl}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: compName,
                    text: shareText,
                    url: shareUrl
                });
            } catch (err) {
                console.log('Megosztás megszakítva:', err);
            }
        } else if (navigator.clipboard) {
            try {
                await navigator.clipboard.writeText(shareText);
                alert('A verseny hivatkozása kimásolva a vágólapra! 🏹');
            } catch (err) {
                console.error('Másolási hiba:', err);
            }
        }
    };

    // --- TÖBBES CSALÁDI NEVEZÉSI METÓDUSOK ---
    const handleOpenRegistrationModal = async () => {
        if (!currentUser) {
            setShowAuthModal(true);
            return;
        }

        try {
            const qFam = query(collection(db, "events", id, "participants"), where("registeredByUid", "==", currentUser.uid));
            const famSnap = await getDocs(qFam);
            const loadedCompetitors = [];

            famSnap.docs.forEach(docSnap => {
                const data = docSnap.data();
                loadedCompetitors.push({
                    uid: data.userId || docSnap.id,
                    lastName: data.lastName || '',
                    firstName: data.firstName || '',
                    fullName: data.fullName || 'Versenyző',
                    club: data.club || '',
                    license: data.license || '',
                    birthDate: data.birthDate || '',
                    gender: data.gender || 'Férfi',
                    bowCategory: data.bowCategory || '',
                    ageCategory: data.ageCategory || '',
                    lunchCount: data.lunchCount || 0,
                    lunchSelections: data.lunchSelections || { A: 0, B: 0, C: 0 },
                    comment: data.comment || ''
                });
            });

            if (loadedCompetitors.length === 0) {
                const partRef = doc(db, "events", id, "participants", currentUser.uid);
                const partSnap = await getDoc(partRef);
                if (partSnap.exists()) {
                    const data = partSnap.data();
                    const mainName = userProfile ? `${userProfile.lastName || ''} ${userProfile.firstName || ''}`.trim() : 'Saját magam';
                    loadedCompetitors.push({
                        uid: currentUser.uid,
                        lastName: data.lastName || userProfile?.lastName || '',
                        firstName: data.firstName || userProfile?.firstName || '',
                        fullName: data.fullName || mainName,
                        club: data.club || userProfile?.clubName || userProfile?.club || '',
                        license: data.license || userProfile?.license || '',
                        birthDate: data.birthDate || userProfile?.birthDate || '',
                        gender: data.gender || 'Férfi',
                        bowCategory: data.bowCategory || '',
                        ageCategory: data.ageCategory || '',
                        lunchCount: data.lunchCount || 0,
                        lunchSelections: data.lunchSelections || { A: 0, B: 0, C: 0 },
                        comment: data.comment || ''
                    });
                }
            }

            if (loadedCompetitors.length > 0) {
                setActiveCompetitorsList(loadedCompetitors);
                setCurrentCompetitorIndex(0);
                displayCompetitorAtIndex(0, loadedCompetitors);
            } else {
                const mainName = userProfile ? `${userProfile.lastName || ''} ${userProfile.firstName || ''}`.trim() : 'Saját magam';
                const defaultComp = {
                    uid: currentUser.uid,
                    lastName: userProfile?.lastName || '',
                    firstName: userProfile?.firstName || '',
                    fullName: mainName || 'Saját magam',
                    club: userProfile?.clubName || userProfile?.club || '',
                    license: userProfile?.license || '',
                    birthDate: userProfile?.birthDate || '',
                    gender: userProfile?.gender || 'Férfi',
                    bowCategory: '',
                    ageCategory: '',
                    lunchCount: 0,
                    lunchSelections: { A: 0, B: 0, C: 0 },
                    comment: ''
                };
                setActiveCompetitorsList([defaultComp]);
                setCurrentCompetitorIndex(0);
                displayCompetitorAtIndex(0, [defaultComp]);
            }

            setSelectedEvent(competition);
            setRegistrationSuccess(false);
            setShowRegModal(true);
        } catch (err) {
            console.error("Hiba a nevezési adatok betöltésekor:", err);
            setShowRegModal(true);
        }
    };

    const saveCurrentFormToActiveCompetitor = () => {
        if (activeCompetitorsList.length === 0 || currentCompetitorIndex < 0 || currentCompetitorIndex >= activeCompetitorsList.length) return;
        setActiveCompetitorsList(prev => {
            const next = [...prev];
            next[currentCompetitorIndex] = {
                ...next[currentCompetitorIndex],
                gender: regData.gender,
                bowCategory: regData.bowCategory,
                ageCategory: regData.ageCategory,
                lunchCount: regData.lunchCount,
                lunchSelections: { ...regData.lunchSelections },
                comment: regData.comment
            };
            return next;
        });
    };

    const displayCompetitorAtIndex = (index, listToUse = activeCompetitorsList) => {
        if (listToUse.length === 0 || index < 0 || index >= listToUse.length) return;
        setCurrentCompetitorIndex(index);
        const comp = listToUse[index];
        setRegData({
            gender: comp.gender || 'Férfi',
            bowCategory: comp.bowCategory || '',
            ageCategory: comp.ageCategory || '',
            lunchCount: comp.lunchCount || 0,
            lunchSelections: comp.lunchSelections || { A: 0, B: 0, C: 0 },
            comment: comp.comment || ''
        });
    };

    const handlePrevCompetitor = () => {
        if (activeCompetitorsList.length <= 1) return;
        saveCurrentFormToActiveCompetitor();
        const nextIdx = (currentCompetitorIndex - 1 + activeCompetitorsList.length) % activeCompetitorsList.length;
        displayCompetitorAtIndex(nextIdx);
    };

    const handleNextCompetitor = () => {
        if (activeCompetitorsList.length <= 1) return;
        saveCurrentFormToActiveCompetitor();
        const nextIdx = (currentCompetitorIndex + 1) % activeCompetitorsList.length;
        displayCompetitorAtIndex(nextIdx);
    };

    const handleOpenSelectPeopleModal = async () => {
        if (!currentUser) return;
        saveCurrentFormToActiveCompetitor();
        try {
            const subSnap = await getDocs(collection(db, "users", currentUser.uid, "subProfiles"));
            const subList = subSnap.docs.map(d => ({
                uid: d.data().linkedUid || d.id,
                lastName: d.data().lastName || '',
                firstName: d.data().firstName || '',
                fullName: d.data().fullName || 'Alprofil',
                club: d.data().club || '',
                license: d.data().license || '',
                birthDate: d.data().birthDate || '',
                gender: d.data().gender || 'Férfi',
                relation: d.data().relation || ''
            }));

            const mainName = userProfile ? `${userProfile.lastName || ''} ${userProfile.firstName || ''}`.trim() : 'Saját magam';
            const allAvail = [
                {
                    uid: currentUser.uid,
                    lastName: userProfile?.lastName || '',
                    firstName: userProfile?.firstName || '',
                    fullName: mainName || 'Saját magam',
                    club: userProfile?.clubName || userProfile?.club || '',
                    license: userProfile?.license || '',
                    birthDate: userProfile?.birthDate || '',
                    gender: userProfile?.gender || 'Férfi',
                    relation: 'Saját magam'
                },
                ...subList
            ];

            setSubProfiles(allAvail);
            setShowSelectPeopleModal(true);
        } catch (err) {
            console.error("Hiba az alprofilok betöltésekor:", err);
        }
    };

    const handleOpenWithdrawModal = async () => {
        if (!currentUser || !id) return;
        try {
            const familyMap = {};

            const qFam = query(collection(db, "events", id, "participants"), where("registeredByUid", "==", currentUser.uid));
            const famSnap = await getDocs(qFam);
            famSnap.docs.forEach(d => {
                const uId = d.data().userId || d.id;
                const fName = d.data().fullName || "Versenyző";
                familyMap[uId] = uId === currentUser.uid ? `👤 ${fName} (Saját magam)` : `👥 ${fName}`;
            });

            const partRef = doc(db, "events", id, "participants", currentUser.uid);
            const partSnap = await getDoc(partRef);
            if (partSnap.exists()) {
                const mUid = partSnap.data().userId || currentUser.uid;
                const mName = partSnap.data().fullName || "Saját magam";
                familyMap[mUid] = `👤 ${mName} (Saját magam)`;
            }

            const list = Object.keys(familyMap).map(uId => ({ uid: uId, name: familyMap[uId] }));
            setRegisteredFamilyForWithdraw(list);
            const selMap = {};
            list.forEach(item => { selMap[item.uid] = false; });
            setSelectedWithdrawUids(selMap);
            setShowWithdrawModal(true);
        } catch (err) {
            console.error("Hiba a lemondási adatok betöltésekor:", err);
        }
    };

    const handleConfirmWithdrawal = async () => {
        if (!currentUser || !id) return;
        const uidsToDelete = Object.keys(selectedWithdrawUids).filter(u => selectedWithdrawUids[u]);
        if (uidsToDelete.length === 0) {
            alert("Nincs kiválasztva lemondandó versenyző!");
            return;
        }

        setIsWithdrawing(true);
        try {
            const batch = writeBatch(db);
            for (const uId of uidsToDelete) {
                const pRef = doc(db, "events", id, "participants", uId);
                batch.delete(pRef);
            }

            const eventRef = doc(db, "events", id);
            batch.update(eventRef, {
                participantsCount: increment(-uidsToDelete.length)
            });

            await batch.commit();
            setWithdrawSuccess(true);
            setTimeout(() => {
                setShowWithdrawModal(false);
                setWithdrawSuccess(false);
                setIsWithdrawing(false);
                window.location.reload();
            }, 1500);
        } catch (err) {
            console.error("Hiba a lemondáskor:", err);
            alert("Hiba történt a lemondás során.");
            setIsWithdrawing(false);
        }
    };

    const handleJoinSubmit = async () => {
        if (checkIsRegistrationClosed(competition)) {
            alert("A nevezés erre a versenyre már lezárult!");
            return;
        }

        // AKTUÁLIS FORM ADATOK RÁMENTÉSE AZ ÉPPEN MEGJELENÍTETT VERSENYZŐRE
        let listToSubmit = activeCompetitorsList.map((comp, idx) => {
            if (idx === currentCompetitorIndex) {
                return {
                    ...comp,
                    gender: regData.gender,
                    bowCategory: regData.bowCategory,
                    ageCategory: regData.ageCategory,
                    lunchCount: regData.lunchCount,
                    lunchSelections: { ...regData.lunchSelections },
                    comment: regData.comment
                };
            }
            return comp;
        });

        if (listToSubmit.length === 0) {
            const mainName = userProfile ? `${userProfile.lastName || ''} ${userProfile.firstName || ''}`.trim() : 'Saját magam';
            listToSubmit = [{
                uid: currentUser.uid,
                lastName: userProfile?.lastName || '',
                firstName: userProfile?.firstName || '',
                fullName: mainName,
                club: userProfile?.clubName || userProfile?.club || '',
                license: userProfile?.license || '',
                birthDate: userProfile?.birthDate || '',
                gender: regData.gender,
                bowCategory: regData.bowCategory,
                ageCategory: regData.ageCategory,
                lunchCount: regData.lunchCount,
                lunchSelections: { ...regData.lunchSelections },
                comment: regData.comment
            }];
        }

        for (let i = 0; i < listToSubmit.length; i++) {
            const comp = listToSubmit[i];
            if (!comp.bowCategory || !comp.ageCategory) {
                displayCompetitorAtIndex(i, listToSubmit);
                alert(`Kérlek válassz íjkategóriát és korosztályt ${comp.fullName} részére!`);
                return;
            }
        }

        setIsSubmitting(true);
        try {
            let newParticipantsCount = 0;
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
            const platform = isIOS ? "ios" : "web";
            const batch = writeBatch(db);

            for (const comp of listToSubmit) {
                const chosenBowCategories = Array.from(new Set([
                    comp.bowCategory,
                    ...additionalBowCategories.filter(c => c && c.trim() !== '')
                ]));

                for (let idx = 0; idx < chosenBowCategories.length; idx++) {
                    const catCode = chosenBowCategories[idx];
                    const docId = idx === 0 ? comp.uid : `${comp.uid}_${catCode}`;
                    const partRef = doc(db, "events", id, "participants", docId);
                    const partSnap = await getDoc(partRef);
                    if (idx === 0 && !partSnap.exists()) {
                        newParticipantsCount++;
                    }

                    const entryFee = competition?.ageCategoriesFees?.[comp.ageCategory] || '';
                    const regPayload = {
                        userId: comp.uid,
                        registeredByUid: currentUser.uid,
                        lastName: comp.lastName,
                        firstName: comp.firstName,
                        fullName: comp.fullName,
                        club: formatClubName(comp.club),
                        license: comp.license,
                        gender: comp.gender || 'Férfi',
                        birthDate: comp.birthDate,
                        bowCategory: catCode,
                        ageCategory: comp.ageCategory,
                        entryFee: String(entryFee),
                        lunchCount: idx === 0 ? (comp.lunchCount || 0) : 0,
                        lunchSelections: idx === 0 ? (comp.lunchSelections || { A: 0, B: 0, C: 0 }) : { A: 0, B: 0, C: 0 },
                        comment: comp.comment || '',
                        registrationDate: serverTimestamp(),
                        status: "Nevezve",
                        isManuallyEdited: false,
                        registeredVia: platform
                    };

                    batch.set(partRef, regPayload);
                }

                if (comp.uid !== currentUser.uid) {
                    try {
                        await updateDoc(doc(db, "users", currentUser.uid, "subProfiles", comp.uid), {
                            gender: comp.gender || 'Férfi'
                        });
                    } catch (e) {}
                }
            }

            if (newParticipantsCount > 0) {
                const eventRef = doc(db, "events", id);
                batch.update(eventRef, {
                    participantsCount: increment(newParticipantsCount)
                });
            }

            await batch.commit();

            setRegistrationSuccess(true);
            setTimeout(() => {
                setShowRegModal(false);
                setRegistrationSuccess(false);
                setIsSubmitting(false);
                window.location.reload();
            }, 1500);
        } catch (err) {
            console.error("Hiba a nevezés beküldésekor:", err);
            alert("Hiba történt a nevezés beküldése során.");
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const fetchCompetition = async () => {
            try {
                // 1. Először megpróbáljuk az aktív versenyek közül
                let docRef = doc(db, "events", id);
                let docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    setCompetition({ id: docSnap.id, ...docSnap.data() });
                } else {
                    // 2. Ha ott nincs, megnézzük a lezártak között
                    docRef = doc(db, "events_closed", id);
                    docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        setCompetition({ id: docSnap.id, ...docSnap.data() });
                    } else {
                        alert("A verseny nem található!");
                        navigate('/versenyek');
                    }
                }
            } catch (error) {
                console.error("Hiba a verseny betöltésekor:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchCompetition();
    }, [id, navigate]);

    useEffect(() => {
        if ((activeTab === 'participants' || activeTab === 'teams') && participants.length === 0) {
            const fetchParticipants = async () => {
                setLoadingParticipants(true);
                try {
                    const partsRef = collection(db, "events", id, "participants");
                    const querySnapshot = await getDocs(partsRef);
                    const partsData = querySnapshot.docs.map(doc => doc.data());
                    setParticipants(partsData);
                } catch (error) {
                    console.error("Hiba a nevezők betöltésekor:", error);
                } finally {
                    setLoadingParticipants(false);
                }
            };
            fetchParticipants();
        }
    }, [activeTab, id, participants.length]);

    const publishedTeamsData = useMemo(() => {
        const teamMap = {};
        participants.forEach(p => {
            if (p.team) {
                const tNum = Number(p.team);
                if (!teamMap[tNum]) {
                    teamMap[tNum] = [];
                }
                teamMap[tNum].push(p);
            }
        });

        return Object.keys(teamMap)
            .map(Number)
            .sort((a, b) => a - b)
            .map(tNum => ({
                teamNumber: tNum,
                members: teamMap[tNum].sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '', 'hu'))
            }));
    }, [participants]);

    const groupedParticipants = useMemo(() => {
        const groups = {};
        participants.forEach(p => {
            const bowCat = p.bowCategory || 'egyeb_altalanos';
            const ageCat = p.ageCategory || 'felnott';
            const gender = p.gender || 'Férfi';

            const key = `${bowCat}|${ageCat}|${gender}`;
            if (!groups[key]) {
                groups[key] = {
                    bowCategory: bowCat,
                    ageCategory: ageCat,
                    gender: gender,
                    members: []
                };
            }
            groups[key].members.push(p);
        });

        return Object.values(groups).sort((a, b) => {
            // 1. Íjkategória (ABC)
            const bowA = translationMap[a.bowCategory] || a.bowCategory || '';
            const bowB = translationMap[b.bowCategory] || b.bowCategory || '';
            const bowComp = (bowA || '').localeCompare(bowB || '', 'hu');
            if (bowComp !== 0) return bowComp;

            // 2. Korosztály (Súlyozott sorrend a globális konstansból)
            const ageA = AGE_ORDER[a.ageCategory] || 99;
            const ageB = AGE_ORDER[b.ageCategory] || 99;
            if (ageA !== ageB) return ageA - ageB;

            // 3. Nem (Nők előre)
            const genA = a.gender === 'Nő' ? 0 : 1;
            const genB = b.gender === 'Nő' ? 0 : 1;
            return genA - genB;
        });
    }, [participants]);

    const handleWithdrawSubmit = async () => {
        if (!currentUser || !id) return;
        setIsWithdrawing(true);
        try {
            const batch = writeBatch(db);
            const eventRef = doc(db, "events", id);

            const partsSnap = await getDocs(collection(db, "events", id, "participants"));
            let deletedDocsCount = 0;

            partsSnap.docs.forEach(dDoc => {
                const docData = dDoc.data();
                if (dDoc.id === currentUser.uid || dDoc.id.startsWith(`${currentUser.uid}_`) || docData.userId === currentUser.uid) {
                    batch.delete(dDoc.ref);
                    deletedDocsCount++;
                }
            });

            if (deletedDocsCount > 0) {
                batch.update(eventRef, {
                    participantsCount: increment(-1)
                });
            }

            // --- ÚJ: NAPTÁR BEJEGYZÉS TÖRLÉSE (WEB) ---
            const compDate = competition.competitionDate || competition.startDate;
            if (compDate) {
                // JAVÍTÁS: A verseny saját ID-ját használjuk a törléshez is
                const calendarRef = doc(db, "users", currentUser.uid, "competitions", id);
                batch.delete(calendarRef);
            }

            await batch.commit();
            setIsJoined(false);
            setWithdrawSuccess(true);

            // Frissítjük a résztvevők listáját ha a tabon vagyunk
            if (activeTab === 'participants') {
                setParticipants(prev => prev.filter(p => p.userId !== currentUser.uid));
            }

            // 2 másodperc múlva bezárjuk az ablakot
            setTimeout(() => {
                setShowWithdrawModal(false);
                setWithdrawSuccess(false);
            }, 2000);

        } catch (error) {
            console.error("Hiba a visszavonáskor:", error);
            alert("Hiba történt a művelet során.");
        } finally {
            setIsWithdrawing(false);
        }
    };

    if (loading) {
        return <div style={pageContainerStyle}><p style={{textAlign: 'center'}}>Betöltés... 🏹</p></div>;
    }

    if (!competition) return null;

    const typeDisplay = getCompetitionTypeDisplay(competition.competitionType);

    return (
        <div className="force-light-theme" style={{ ...pageContainerStyle, padding: isMobile ? '0' : '2rem', maxWidth: isMobile ? '100%' : '1120px' }}>
            {/* --- NEVEZÉSI MODAL --- */}
            {showRegModal && (
                <div style={modalOverlayStyle}>
                    <div style={modalContentStyle(isMobile)}>
                        <div style={modalHeaderStyle(isMobile)}>
                            <h3>Nevezés a versenyre</h3>
                            <div
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: '#f2f2f2',
                                    borderRadius: '50%',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onClick={() => setShowRegModal(false)}
                            >
                                <FiX size={20} color="#333" />
                            </div>
                        </div>

                        <div style={modalBodyStyle(isMobile)}>
                            {registrationSuccess ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '40px 0',
                                    gap: '15px'
                                }}>
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        backgroundColor: '#e6fffa',
                                        color: '#28a745',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '2.5rem',
                                        boxShadow: '0 4px 10px rgba(40, 167, 69, 0.2)'
                                    }}>
                                        <FiCheck />
                                    </div>
                                    <h2 style={{ color: '#28a745', margin: 0 }}>Sikeres nevezés!</h2>
                                    <p style={{ color: '#666', fontSize: '0.9rem' }}>Adataidat rögzítettük a versenyre.</p>
                                </div>
                            ) : (
                                <>
                                    <p style={{ textAlign: 'center', color: '#666', fontSize: '0.85rem', marginBottom: '15px' }}>
                                        Kérjük, ellenőrizze adatait
                                    </p>

                                    {/* PROFIL KÁRTYA */}
                                    <div style={profileRegCardStyle}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                                                <span style={{ fontWeight: 'bold', fontSize: '1.05rem', color: '#111' }}>
                                                    {activeCompetitorsList[currentCompetitorIndex]?.fullName || `${userProfile?.lastName || ''} ${userProfile?.firstName || ''}`.trim() || 'Saját magam'}
                                                </span>
                                                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '3px' }}>
                                                    {activeCompetitorsList[currentCompetitorIndex]?.club || userProfile?.clubName || userProfile?.club || 'Nincs egyesület'}
                                                </div>
                                            </div>

                                            {/* PIROS + GOMB ALPROFILOK CÍMKÉVEL */}
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <button
                                                    type="button"
                                                    onClick={handleOpenSelectPeopleModal}
                                                    style={{
                                                        width: '32px',
                                                        height: '32px',
                                                        backgroundColor: '#D32F2F',
                                                        color: '#fff',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        fontWeight: 'bold',
                                                        fontSize: '1.2rem',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 2px 5px rgba(211,47,47,0.3)'
                                                    }}
                                                    title="Alprofil választás"
                                                >
                                                    +
                                                </button>
                                                <span style={{ fontSize: '0.65rem', color: '#888', marginTop: '2px', fontWeight: 'bold' }}>Alprofilok</span>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                                            <button
                                                type="button"
                                                style={genderToggleStyle}
                                                onClick={() => {
                                                    const nextG = regData.gender === 'Férfi' ? 'Nő' : 'Férfi';
                                                    setRegData(prev => ({ ...prev, gender: nextG }));
                                                }}
                                            >
                                                {regData.gender === 'Férfi' ? 'Férfi' : 'Nő'} <FiChevronRight />
                                            </button>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#888' }}>SZÜLETÉSI DÁTUM</div>
                                                <div style={{ fontSize: '0.9rem' }}>
                                                    {activeCompetitorsList[currentCompetitorIndex]?.birthDate || (userProfile?.birthDate ? new Date(userProfile.birthDate).toLocaleDateString('hu-HU') : '-')}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* CAROUSEL NAVIGÁCIÓS SÁV (HA TÖBB MINT 1 CSALÁDTAG VAN KIVÁLASZTVA) */}
                                    {activeCompetitorsList.length > 1 && (
                                        <div style={{
                                            display: 'flex',
                                            justify: 'space-between',
                                            alignItems: 'center',
                                            backgroundColor: '#f8f9fa',
                                            padding: '8px 12px',
                                            borderRadius: '8px',
                                            border: '1px solid #eee',
                                            margin: '12px 0 16px 0'
                                        }}>
                                            <button
                                                type="button"
                                                onClick={handlePrevCompetitor}
                                                style={{ border: 'none', background: 'none', color: '#D32F2F', fontWeight: 'bold', fontSize: '1.2rem', cursor: 'pointer' }}
                                            >
                                                ◄
                                            </button>
                                            <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#333' }}>
                                                Versenyző: {currentCompetitorIndex + 1} / {activeCompetitorsList.length} ({activeCompetitorsList[currentCompetitorIndex]?.fullName})
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleNextCompetitor}
                                                style={{ border: 'none', background: 'none', color: '#D32F2F', fontWeight: 'bold', fontSize: '1.2rem', cursor: 'pointer' }}
                                            >
                                                ►
                                            </button>
                                        </div>
                                    )}

                                    <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '16px 0' }} />

                                    {/* ÍJKATEGÓRIA VÁLASZTÓ */}
                                    <div style={inputGroupStyle}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                            <label style={regLabelStyle}>Válassz íjkategóriát</label>
                                            {(activeCompetitorsList[currentCompetitorIndex]?.license || userProfile?.license) && (
                                                <span style={{ fontSize: '0.7rem', color: '#888', fontWeight: 'bold' }}>
                                                    Engedély: {activeCompetitorsList[currentCompetitorIndex]?.license || userProfile.license}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <select
                                                style={{ ...regSelectStyle, flex: 1, margin: 0 }}
                                                value={regData.bowCategory}
                                                onChange={(e) => setRegData(prev => ({ ...prev, bowCategory: e.target.value }))}
                                            >
                                                <option value="">Válassz kategóriát...</option>
                                                {competition?.categories?.map(cat => (
                                                    <option key={cat} value={cat}>{translationMap[cat] || cat}</option>
                                                ))}
                                            </select>

                                            {additionalBowCategories.length < 1 && (
                                                <button
                                                    type="button"
                                                    style={{
                                                      display: 'none', // IDEIGLENESEN REJTVE WEBEN
                                                      width: '38px',
                                                      height: '38px',
                                                      borderRadius: '8px',
                                                      border: 'none',
                                                      backgroundColor: '#b30000',
                                                      color: 'white',
                                                      fontWeight: 'bold',
                                                      fontSize: '1.2rem',
                                                      cursor: 'pointer',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      boxShadow: '0 2px 6px rgba(179,0,0,0.25)',
                                                      transition: 'transform 0.2s',
                                                      flexShrink: 0
                                                    }}
                                                    onClick={() => setAdditionalBowCategories(prev => [...prev, ''])}
                                                    title="Második íjkategória hozzáadása"
                                                >
                                                    <FiPlus />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* TOVÁBBI ÍJKATEGÓRIÁK */}
                                    {additionalBowCategories.map((addCat, addIdx) => (
                                      <div key={`add-cat-${addIdx}`} style={{ ...inputGroupStyle, display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <select
                                            style={{ ...regSelectStyle, flex: 1, margin: 0 }}
                                            value={addCat}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setAdditionalBowCategories(prev => {
                                                    const next = [...prev];
                                                    next[addIdx] = val;
                                                    return next;
                                                });
                                            }}
                                        >
                                            <option value="">Válassz további kategóriát...</option>
                                            {competition?.categories?.map(cat => (
                                                <option key={cat} value={cat}>{translationMap[cat] || cat}</option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={() => setAdditionalBowCategories(prev => prev.filter((_, i) => i !== addIdx))}
                                            style={{
                                                backgroundColor: '#fff0f0',
                                                color: '#b30000',
                                                border: '1px solid #ffcccc',
                                                borderRadius: '8px',
                                                width: '38px',
                                                height: '38px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                fontWeight: 'bold',
                                                flexShrink: 0
                                            }}
                                            title="Kategória törlése"
                                        >
                                            <FiX size={18} />
                                        </button>
                                      </div>
                                    ))}

                                    {/* KOROSZTÁLY VÁLASZTÓ */}
                                    <div style={inputGroupStyle}>
                                        <label style={regLabelStyle}>Válassz korosztályt</label>
                                        <select
                                            style={regSelectStyle}
                                            value={regData.ageCategory}
                                            onChange={(e) => setRegData(prev => ({ ...prev, ageCategory: e.target.value }))}
                                        >
                                            <option value="">Válassz korosztályt...</option>
                                            {competition?.ageCategories?.map(age => (
                                                <option key={age} value={age}>{translationMap[age] || age}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* EBÉD (HA ELÉRHETŐ) */}
                                    {competition?.lunchAvailable && (
                                        <div style={inputGroupStyle}>
                                            <label style={regLabelStyle}>{competition.lunchInEntryFee ? "Vendég ebéd (opcionális extra):" : "Ebéd igény (adag):"}</label>

                                            {competition.lunchInEntryFee ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '5px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fdfdfd', padding: '8px 12px', borderRadius: '10px', border: '1px solid #eee' }}>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                Vendég ebéd{competition.lunches?.['A']?.name ? `: ${competition.lunches['A'].name}` : ''}
                                                            </div>
                                                            <div style={{ fontSize: '0.75rem', color: '#888' }}>{competition.lunches?.['A']?.price || competition.lunchPrice || 0} Ft / adag</div>
                                                        </div>
                                                        <div style={lunchCounterStyle}>
                                                            <button
                                                                type="button"
                                                                style={lunchBtnStyle}
                                                                onClick={() => {
                                                                    const newVal = Math.max(0, (regData.lunchSelections?.['A'] || 0) - 1);
                                                                    const newSelections = { ...regData.lunchSelections, A: newVal };
                                                                    const newTotal = Object.values(newSelections).reduce((a, b) => a + b, 0);
                                                                    setRegData(prev => ({ ...prev, lunchSelections: newSelections, lunchCount: newTotal }));
                                                                }}
                                                            >
                                                                <FiMinus />
                                                            </button>
                                                            <span style={{ width: '30px', textAlign: 'center', fontWeight: 'bold', fontSize: '0.9rem' }}>{regData.lunchSelections?.['A'] || 0}</span>
                                                            <button
                                                                type="button"
                                                                style={lunchBtnStyle}
                                                                onClick={() => {
                                                                    const newVal = (regData.lunchSelections?.['A'] || 0) + 1;
                                                                    const newSelections = { ...regData.lunchSelections, A: newVal };
                                                                    const newTotal = Object.values(newSelections).reduce((a, b) => a + b, 0);
                                                                    setRegData(prev => ({ ...prev, lunchSelections: newSelections, lunchCount: newTotal }));
                                                                }}
                                                            >
                                                                <FiPlus />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                competition.lunches && (competition.lunches.A?.price || competition.lunches.B?.price || competition.lunches.C?.price || competition.lunches.B?.name || competition.lunches.C?.name) ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '5px' }}>
                                                    {['A', 'B', 'C'].map((menuKey) => {
                                                        const menu = competition.lunches[menuKey];
                                                        if (!menu || (!menu.name && !menu.price)) return null;
                                                        const displayName = menu.name ? `${menuKey}. menü: ${menu.name}` : `Ebéd (${menuKey}. menü)`;

                                                        return (
                                                            <div key={menuKey} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fdfdfd', padding: '8px 12px', borderRadius: '10px', border: '1px solid #eee' }}>
                                                                <div style={{ flex: 1 }}>
                                                                    <div style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{displayName}</div>
                                                                    <div style={{ fontSize: '0.75rem', color: '#888' }}>{menu.price || 0} Ft / adag</div>
                                                                </div>
                                                                <div style={lunchCounterStyle}>
                                                                    <button
                                                                        type="button"
                                                                        style={lunchBtnStyle}
                                                                        onClick={() => {
                                                                            const newVal = Math.max(0, (regData.lunchSelections?.[menuKey] || 0) - 1);
                                                                            const newSelections = { ...regData.lunchSelections, [menuKey]: newVal };
                                                                            const newTotal = Object.values(newSelections).reduce((a, b) => a + b, 0);
                                                                            setRegData(prev => ({ ...prev, lunchSelections: newSelections, lunchCount: newTotal }));
                                                                        }}
                                                                    >
                                                                        <FiMinus />
                                                                    </button>
                                                                    <span style={{ width: '30px', textAlign: 'center', fontWeight: 'bold', fontSize: '0.9rem' }}>{regData.lunchSelections?.[menuKey] || 0}</span>
                                                                    <button
                                                                        type="button"
                                                                        style={lunchBtnStyle}
                                                                        onClick={() => {
                                                                            const newVal = (regData.lunchSelections?.[menuKey] || 0) + 1;
                                                                            const newSelections = { ...regData.lunchSelections, [menuKey]: newVal };
                                                                            const newTotal = Object.values(newSelections).reduce((a, b) => a + b, 0);
                                                                            setRegData(prev => ({ ...prev, lunchSelections: newSelections, lunchCount: newTotal }));
                                                                        }}
                                                                    >
                                                                        <FiPlus />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div style={lunchCounterStyle}>
                                                    <button
                                                        type="button"
                                                        style={lunchBtnStyle}
                                                        onClick={() => setRegData(prev => {
                                                            const newCount = Math.max(0, prev.lunchCount - 1);
                                                            return {
                                                                ...prev,
                                                                lunchCount: newCount,
                                                                lunchSelections: { ...prev.lunchSelections, A: newCount }
                                                            };
                                                        })}
                                                    >
                                                        <FiMinus />
                                                    </button>
                                                    <span style={{ width: '40px', textAlign: 'center', fontWeight: 'bold' }}>{regData.lunchCount}</span>
                                                    <button
                                                        type="button"
                                                        style={lunchBtnStyle}
                                                        onClick={() => setRegData(prev => {
                                                            const newCount = prev.lunchCount + 1;
                                                            return {
                                                                ...prev,
                                                                lunchCount: newCount,
                                                                lunchSelections: { ...prev.lunchSelections, A: newCount }
                                                            };
                                                        })}
                                                    >
                                                        <FiPlus />
                                                    </button>
                                                </div>
                                            )
                                            )}
                                        </div>
                                    )}

                                    {/* MEGJEGYZÉS */}
                                    <div style={inputGroupStyle}>
                                        <label style={regLabelStyle}>Megjegyzés a szervezőnek:</label>
                                        <textarea
                                            style={regTextAreaStyle}
                                            placeholder="Pl. csapattárs kérés..."
                                            value={regData.comment}
                                            onChange={(e) => setRegData(prev => ({ ...prev, comment: e.target.value }))}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {!registrationSuccess && (
                            <div style={modalFooterStyle(isMobile)}>
                                <button
                                    style={{ ...regSubmitButtonStyle, marginTop: 0 }}
                                    onClick={handleJoinSubmit}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Mentés...' : (activeCompetitorsList.length > 1 ? `Nevezések beküldése (${activeCompetitorsList.length} fő)` : (isJoined ? 'Nevezés módosítása' : 'Nevezés leadása'))}
                                </button>

                                <p style={{ fontSize: '0.65rem', color: '#888', textAlign: 'center', marginTop: '10px', lineHeight: '1.4', marginBottom: 0 }}>
                                    *A "Nevezés beküldése" gombra kattintva kijelented, hogy elolvastad és elfogadtad a versenyszabályzatot!
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* TÖBBES VERSENYZŐ VÁLASZTÓ MODAL */}
            {showSelectPeopleModal && (
                <div style={modalOverlayStyle}>
                    <div style={modalContentStyle(isMobile)}>
                        <div style={modalHeaderStyle(isMobile)}>
                            <h3 style={{ margin: 0, color: '#D32F2F' }}>Válassz versenyzőket</h3>
                            <button style={modalCloseButtonStyle} onClick={() => setShowSelectPeopleModal(false)}><FiX /></button>
                        </div>
                        <div style={modalBodyStyle(isMobile)}>
                            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '15px' }}>
                                Pipáld be azokat a családtagokat/alprofilokat, akiket be szeretnél nevezni:
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                                {subProfiles.map((comp, idx) => {
                                    const isChecked = activeCompetitorsList.some(c => c.uid === comp.uid);
                                    return (
                                        <label
                                            key={comp.uid}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                padding: '12px 14px',
                                                backgroundColor: '#f8f9fa',
                                                borderRadius: '8px',
                                                border: '1px solid #eee',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={(e) => {
                                                    const val = e.target.checked;
                                                    setActiveCompetitorsList(prev => {
                                                        if (val) {
                                                            if (!prev.some(c => c.uid === comp.uid)) return [...prev, comp];
                                                            return prev;
                                                        } else {
                                                            return prev.filter(c => c.uid !== comp.uid);
                                                        }
                                                    });
                                                }}
                                            />
                                            <span style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                                {idx === 0 ? `👤 ${comp.fullName} (Saját magam)` : `👥 ${comp.fullName}`}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        <div style={modalFooterStyle(isMobile)}>
                            <button
                                style={{ ...regSubmitButtonStyle, backgroundColor: '#D32F2F' }}
                                onClick={() => {
                                    if (activeCompetitorsList.length === 0 && subProfiles.length > 0) {
                                        setActiveCompetitorsList([subProfiles[0]]);
                                    }
                                    setCurrentCompetitorIndex(0);
                                    displayCompetitorAtIndex(0, activeCompetitorsList.length > 0 ? activeCompetitorsList : [subProfiles[0]]);
                                    setShowSelectPeopleModal(false);
                                }}
                            >
                                Hozzáadás
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* LEMONDÁSI MODAL BEPIPÁLHATÓ CSALÁDTAGOKKAL */}
            {showWithdrawModal && (
                <div style={modalOverlayStyle}>
                    <div style={modalContentStyle(isMobile)}>
                        <div style={modalHeaderStyle(isMobile)}>
                            <h3 style={{ margin: 0 }}>{withdrawSuccess ? 'Sikeres lemondás' : 'Nevezések lemondása'}</h3>
                            <button style={modalCloseButtonStyle} onClick={() => setShowWithdrawModal(false)}><FiX /></button>
                        </div>
                        <div style={modalBodyStyle(isMobile)}>
                            {withdrawSuccess ? (
                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <div style={{ fontSize: '2.5rem', color: '#28a745', marginBottom: '10px' }}>✓</div>
                                    <p style={{ color: '#666' }}>A kiválasztott nevezés(ek) sikeresen törölve!</p>
                                </div>
                            ) : (
                                <div>
                                    <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '15px' }}>
                                        Melyik családtag(ok) nevezését szeretnéd lemondani?
                                    </p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                                        {registeredFamilyForWithdraw.map(item => (
                                            <label
                                                key={item.uid}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    padding: '10px 14px',
                                                    backgroundColor: '#f8f9fa',
                                                    borderRadius: '8px',
                                                    border: '1px solid #eee',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={!!selectedWithdrawUids[item.uid]}
                                                    onChange={(e) => {
                                                        const val = e.target.checked;
                                                        setSelectedWithdrawUids(prev => ({ ...prev, [item.uid]: val }));
                                                    }}
                                                />
                                                <span style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{item.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {!withdrawSuccess && (
                            <div style={modalFooterStyle(isMobile)}>
                                <button
                                    style={{ ...regSubmitButtonStyle, backgroundColor: '#D32F2F' }}
                                    onClick={handleConfirmWithdrawal}
                                    disabled={isWithdrawing}
                                >
                                    {isWithdrawing ? 'Lemondás...' : 'Kiválasztottak lemondása'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {!isMobile && (
                <button style={backButtonStyle} onClick={() => navigate('/versenyek')}>
                    <FiArrowLeft /> Vissza a versenyekhez
                </button>
            )}

            <div style={{ display: isMobile ? 'block' : 'flex', gap: '24px', alignItems: 'flex-start' }}>
                <div style={{
                    ...cardStyle,
                    flex: 1,
                    padding: isMobile ? '20px 16px 100px 16px' : '2rem',
                    borderRadius: isMobile ? '0' : '12px',
                    boxShadow: isMobile ? 'none' : cardStyle.boxShadow
                }}>
                {/* ASZTALI NÉZET: NEVEZVE INDIKÁTOR */}
                {isJoined && !isMobile && (
                    <div style={{
                        position: 'absolute', top: '2rem', right: '2rem',
                        display: 'flex', alignItems: 'center', gap: '15px'
                    }}>
                        {/* SZERKESZTÉS IKON - CSAK HA NINCS TILTVA */}
                        {!competition.isWithdrawalDisabled && (
                            <button
                                onClick={() => setShowOptionsModal(true)}
                                style={{
                                    background: 'none', border: 'none', color: '#666666',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center'
                                }}
                                title="Nevezés kezelése"
                            >
                                <FiEdit2 size={24} />
                            </button>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#28a745', fontWeight: 'bold', fontSize: '0.9rem' }}>
                            <div style={{ position: 'relative', width: '32px', height: '32px' }}>
                                <FiUserCheck size={32} />
                                <div style={{
                                    position: 'absolute', top: -2, right: -2,
                                    backgroundColor: '#b30000', color: 'white', borderRadius: '50%',
                                    width: '14px', height: '14px', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', fontSize: '10px'
                                }}>
                                    ✓
                                </div>
                            </div>
                            <span>NEVEZVE</span>
                        </div>
                    </div>
                )}

                {/* MOBIL FEJLÉC (Android stílus) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '2px' : '0.5rem' }}>
                    <h1 style={{
                        ...titleStyle,
                        fontSize: isMobile ? '1.5rem' : '2rem',
                        color: isMobile ? '#D32F2F' : titleStyle.color,
                        paddingRight: (isJoined && !isMobile) ? '120px' : '0'
                    }}>
                        {competition.competitionName}
                    </h1>
                    <div style={{
                        ...detailStyle,
                        color: typeDisplay.color,
                        opacity: isMobile ? 0.8 : 1,
                        fontWeight: 'bold',
                        fontSize: isMobile ? '0.9rem' : '1rem'
                    }}>
                        {typeDisplay.label}
                    </div>
                </div>

                <div style={{ ...detailStyle, marginTop: isMobile ? '8px' : '0.5rem', fontSize: isMobile ? '1.1rem' : '1rem' }}>
                    {!isMobile && <FiCalendar />}
                    {competition.competitionDate || competition.startDate}
                    {competition.endDate && ` - ${competition.endDate}`}
                </div>

                <div style={{ ...detailStyle, marginTop: '4px', color: isMobile ? '#D32F2F' : '#555' }}>
                    {!isMobile && <FiMapPin color="#b30000" />}
                    {competition.location}
                </div>

                <div style={{ ...detailStyle, marginTop: isMobile ? '16px' : '0.5rem' }}>
                    {!isMobile && <FiUser />}
                    Szervező: {formatClubName(competition.organizingClub) || 'Nincs megadva'}
                </div>

                {/* NEVEZŐK SZÁMA (Fentebb, a szervező felett/mellett) */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '6px',
                    color: '#b30000',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    marginTop: isMobile ? '12px' : '1rem',
                    marginBottom: isMobile ? '0' : '0'
                }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {competition.location && (
                            <button
                                type="button"
                                onClick={handleNavigateGps}
                                style={{
                                    padding: '6px 14px',
                                    backgroundColor: '#D32F2F',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    boxShadow: '0 2px 5px rgba(211,47,47,0.25)'
                                }}
                            >
                                <FiMapPin size={16} /> Útvonal
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleShareCompetition}
                            title="Verseny megosztása"
                            style={{
                                padding: isMobile ? '6px 10px' : '6px 14px',
                                backgroundColor: '#fff',
                                color: '#D32F2F',
                                border: '1px solid #D32F2F',
                                borderRadius: '8px',
                                fontWeight: 'bold',
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 2px 5px rgba(211,47,47,0.15)'
                            }}
                        >
                            <FiShare2 size={16} /> {!isMobile && 'Megosztás'}
                        </button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FiUserCheck size={18} />
                        <span>Nevezők: {competition.participantsCount || 0} / {competition.maxParticipants || 999}</span>
                    </div>
                </div>

                {/* --- TÁVOK ÉS TÉRKÉP GOMBOK (EGYMÁS MELLETT) --- */}
                {(competition.targetsPublished || isMapVisible) && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '12px',
                    marginTop: '1.5rem',
                    flexWrap: 'nowrap' // Kényszerítjük az egymás mellettiséget
                  }}>
                    {competition.targetsPublished && (
                        <button
                          onClick={() => setShowTargetsModal(true)}
                          style={{
                            backgroundColor: '#b30000',
                            color: '#fff',
                            border: 'none',
                            padding: isMobile ? '8px 15px' : '10px 30px',
                            borderRadius: '25px',
                            fontWeight: 'bold',
                            fontSize: isMobile ? '0.9rem' : '1.1rem',
                            cursor: 'pointer',
                            boxShadow: '0 4px 10px rgba(179, 0, 0, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flex: 1,
                            maxWidth: '200px',
                            justifyContent: 'center'
                          }}
                        >
                          <FiMapPin /> TÁVOK
                        </button>
                    )}

                    {isMapVisible && (
                        <button
                          onClick={() => setShowMapModal(true)}
                          style={{
                            backgroundColor: '#b30000', // MÓDOSÍTVA PIROSRA
                            color: '#fff',
                            border: 'none',
                            padding: isMobile ? '8px 15px' : '10px 30px',
                            borderRadius: '25px',
                            fontWeight: 'bold',
                            fontSize: isMobile ? '0.9rem' : '1.1rem',
                            cursor: 'pointer',
                            boxShadow: '0 4px 10px rgba(179, 0, 0, 0.3)', // PIROS ÁRNYÉK
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flex: 1,
                            maxWidth: '200px',
                            justifyContent: 'center'
                          }}
                        >
                          <FiMapPin /> TÉRKÉP
                        </button>
                    )}
                  </div>
                )}

                {/* ANNOUNCEMENT ÜZENET */}
                {competition.creationType === 'announcement' && (
                  <div style={{
                    backgroundColor: '#fff5f5',
                    border: '1px solid #feb2b2',
                    borderRadius: '8px',
                    padding: '12px 15px',
                    marginTop: '20px',
                    textAlign: 'center',
                    color: '#c53030',
                    fontWeight: 'bold',
                    fontSize: '1.1rem'
                  }}>
                    📢 FIGYELEM: A verseny nevezése még nem nyílt meg!
                  </div>
                )}

                {/* ELVÁLASZTÓ VONAL (Mobilon) */}
                {isMobile && <div style={{ height: '1px', backgroundColor: '#CCCCCC', margin: '16px 0' }} />}

                {/* TAB GOMBOK (Android stílus) */}
                <div style={{
                    ...tabContainerStyle,
                    marginTop: isMobile ? '0' : '2rem',
                    gap: isMobile ? '0' : '20px',
                    borderBottom: isMobile ? 'none' : '2px solid #eee'
                }}>
                    <div
                        style={{
                            flex: isMobile ? 1 : 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer'
                        }}
                        onClick={() => setActiveTab('description')}
                    >
                        <button style={{
                            ...tabButtonStyle(activeTab === 'description'),
                            color: (activeTab === 'description') ? '#D32F2F' : '#666',
                            borderBottom: 'none',
                            padding: isMobile ? '8px' : '10px 20px',
                            fontSize: isMobile ? '13px' : '1rem'
                        }}>
                            Leírás
                        </button>
                        {isMobile && (
                            <div style={{
                                width: '60px',
                                height: '3px',
                                backgroundColor: '#D32F2F',
                                visibility: activeTab === 'description' ? 'visible' : 'hidden'
                            }} />
                        )}
                    </div>

                    <div
                        style={{
                            flex: isMobile ? 1 : 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer'
                        }}
                        onClick={() => setActiveTab('participants')}
                    >
                        <button style={{
                            ...tabButtonStyle(activeTab === 'participants'),
                            color: (activeTab === 'participants') ? '#D32F2F' : '#666',
                            borderBottom: 'none',
                            padding: isMobile ? '8px' : '10px 20px',
                            fontSize: isMobile ? '13px' : '1rem'
                        }}>
                            Nevezők ({competition.participantsCount || participants.length || 0})
                        </button>
                        {isMobile && (
                            <div style={{
                                width: '60px',
                                height: '3px',
                                backgroundColor: '#D32F2F',
                                visibility: activeTab === 'participants' ? 'visible' : 'hidden'
                            }} />
                        )}
                    </div>

                    {/* CSAPATOK TAB (CSAK HA CSAPATOK PUBLIKÁLVA VANNAK!) */}
                    {competition.teamsPublished && (
                        <div
                            style={{
                                flex: isMobile ? 1 : 'none',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                cursor: 'pointer'
                            }}
                            onClick={() => setActiveTab('teams')}
                        >
                            <button style={{
                                ...tabButtonStyle(activeTab === 'teams'),
                                color: (activeTab === 'teams') ? '#D32F2F' : '#666',
                                borderBottom: 'none',
                                padding: isMobile ? '8px' : '10px 20px',
                                fontSize: isMobile ? '13px' : '1rem'
                            }}>
                                Csapatok
                            </button>
                            {isMobile && (
                                <div style={{
                                    width: '60px',
                                    height: '3px',
                                    backgroundColor: '#D32F2F',
                                    visibility: activeTab === 'teams' ? 'visible' : 'hidden'
                                }} />
                            )}
                        </div>
                    )}
                </div>

                {/* LEÍRÁS TARTALOM */}
                {activeTab === 'description' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {competition.shortNotice && competition.status !== 'Lezárt' && (
                            <div style={{
                                backgroundColor: '#fff9c4',
                                border: '2px solid #fbc02d',
                                borderRadius: '10px',
                                padding: '15px 20px',
                                color: '#5f4b00',
                                fontWeight: '900',
                                fontSize: '0.95rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                boxShadow: '0 2px 8px rgba(251, 192, 45, 0.15)'
                            }}>
                                <FiAlertTriangle size={24} style={{ flexShrink: 0 }} />
                                <span>{competition.shortNotice}</span>
                            </div>
                        )}
                        <div
                            className="competition-description-html"
                            style={{ ...descriptionStyle, padding: isMobile ? '0' : '0' }}
                            dangerouslySetInnerHTML={{ __html: competition.competitionDescription || 'Nincs leírás megadva.' }}
                        />
                    </div>
                )}

                {/* NEVEZŐK TARTALOM */}
                {activeTab === 'participants' && (
                    <div>
                        {loadingParticipants ? (
                            <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>Nevezők betöltése... 🏹</p>
                        ) : participants.length === 0 ? (
                            <p style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Még nincs nevező erre a versenyre.</p>
                        ) : (
                            groupedParticipants.map((group, idx) => (
                                <div key={idx} style={{ marginBottom: isMobile ? '1rem' : '2rem' }}>
                                    <div style={{
                                        ...participantHeaderStyle,
                                        marginTop: isMobile ? '1rem' : '1.5rem',
                                        fontSize: isMobile ? '0.85rem' : '0.9rem'
                                    }}>
                                        {translationMap[group.bowCategory] || group.bowCategory} - {translationMap[group.ageCategory] || group.ageCategory} - {group.gender}
                                    </div>
                                    {group.members.sort((a,b) => {
                                        const nameA = a.fullName || a.name || `${a.lastName || ''} ${a.firstName || ''}`.trim() || 'Névtelen nevező';
                                        const nameB = b.fullName || b.name || `${b.lastName || ''} ${b.firstName || ''}`.trim() || 'Névtelen nevező';
                                        return nameA.localeCompare(nameB, 'hu');
                                    }).map((p, pIdx) => {
                                        const displayName = p.fullName || p.name || `${p.lastName || ''} ${p.firstName || ''}`.trim() || 'Névtelen nevező';
                                        const displayClub = formatClubName(p.club) || 'Egyéni induló';
                                        return (
                                        <div key={pIdx} style={{
                                            ...participantRowStyle,
                                            paddingVertical: isMobile ? '10px' : '10px',
                                            paddingHorizontal: isMobile ? '16px' : '15px'
                                        }}>
                                            <span style={{
                                                fontWeight: 'bold',
                                                color: '#333',
                                                fontSize: isMobile ? '16px' : '1rem'
                                            }}>
                                                {displayName}
                                            </span>
                                            <span style={{
                                                fontSize: isMobile ? '12px' : '0.75rem',
                                                color: '#666',
                                                marginTop: isMobile ? '1px' : '0'
                                            }}>
                                                {displayClub}
                                            </span>
                                        </div>
                                    )})}
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* CSAPATOK TARTALOM (CSAPATBEOSZTÁSI RAJTLISTA) */}
                {activeTab === 'teams' && competition.teamsPublished && (
                    <div>
                        {loadingParticipants ? (
                            <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>Csapatbeosztás betöltése... 🏹</p>
                        ) : publishedTeamsData.length === 0 ? (
                            <p style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Még nincsenek beosztott csapatok ezen a versenyen.</p>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))', gap: '15px', marginTop: '10px' }}>
                                {publishedTeamsData.map(team => (
                                    <div key={team.teamNumber} style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                        <div style={{ backgroundColor: '#fff5f5', borderBottom: '2px solid #D32F2F', padding: '10px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontWeight: 'bold', color: '#D32F2F', fontSize: '1rem' }}>
                                                {team.teamNumber}. csapat
                                            </span>
                                            <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: 'bold' }}>
                                                {team.members.length} fő
                                            </span>
                                        </div>
                                        <div style={{ padding: '8px 12px' }}>
                                            {team.members.map(m => (
                                                <div key={m.id || m.userId} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div>
                                                        <div style={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#333' }}>
                                                            {m.fullName}
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: '#888' }}>
                                                            {formatClubName(m.club) || 'Egyéni induló'}
                                                        </div>
                                                    </div>
                                                    <span style={{ backgroundColor: '#f0f0f0', color: '#444', padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                        {translationMap[m.bowCategory] || m.bowCategory}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* VISSZA GOMB (Mobilon alul a listában) */}
                {isMobile && (
                    <button
                        style={{
                            width: '100%',
                            background: 'none',
                            border: 'none',
                            color: '#666',
                            padding: '16px',
                            fontSize: '1rem',
                            marginTop: '8px'
                        }}
                        onClick={() => navigate('/versenyek')}
                    >
                        Vissza
                    </button>
                )}
            </div>

            {/* DESKTOP STICKY SIDEBAR (JOBB OLDALI NEVEZÉSI SÁV) */}
            {!isMobile && !isCheckingUserType && !isClubAccount && (
                <div style={{
                    width: '320px',
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    position: 'sticky',
                    top: '2rem',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                }}>
                        <h3 style={{ margin: 0, color: '#D32F2F', fontSize: '1.2rem', fontWeight: 'bold', borderBottom: '2px solid #fff5f5', paddingBottom: '10px' }}>
                            Nevezés a versenyre
                        </h3>

                        <div style={{ fontSize: '0.9rem', color: '#555', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8f9fa', padding: '10px 14px', borderRadius: '8px' }}>
                            <span>Nevezők száma:</span>
                            <strong style={{ color: '#D32F2F', fontSize: '1rem' }}>{competition.participantsCount || 0} / {competition.maxParticipants || 999}</strong>
                        </div>

                        {checkIsRegistrationClosed(competition) ? (
                            <div style={{ backgroundColor: '#f5f5f5', color: '#666', padding: '14px', borderRadius: '10px', textAlign: 'center', fontWeight: 'bold', fontSize: '0.95rem' }}>
                                NEVEZÉS LEZÁRULT
                            </div>
                        ) : isJoined ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    color: '#28a745',
                                    fontWeight: 'bold',
                                    fontSize: '0.95rem',
                                    backgroundColor: '#e6fffa',
                                    padding: '12px',
                                    borderRadius: '10px',
                                    border: '1px solid #b2f2bb'
                                }}>
                                    <FiUserCheck size={20} />
                                    <span>SIKERESEN NEVEZVE ✓</span>
                                </div>

                                {!competition.isWithdrawalDisabled && (
                                    <button
                                        onClick={() => setShowOptionsModal(true)}
                                        style={{
                                            width: '100%',
                                            backgroundColor: '#f5f5f5',
                                            color: '#333',
                                            border: '1px solid #ddd',
                                            padding: '12px',
                                            borderRadius: '10px',
                                            fontWeight: 'bold',
                                            fontSize: '0.9rem',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Nevezés kezelése / Módosítása
                                    </button>
                                )}
                            </div>
                        ) : (
                            <button
                                style={{
                                    width: '100%',
                                    backgroundColor: competition.creationType === 'announcement' ? '#ccc' : '#D32F2F',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '10px',
                                    padding: '14px',
                                    fontWeight: 'bold',
                                    fontSize: '1.05rem',
                                    cursor: competition.creationType === 'announcement' ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 4px 10px rgba(211,47,47,0.3)'
                                }}
                                disabled={competition.creationType === 'announcement'}
                                onClick={() => {
                                    if (!currentUser) {
                                        setShowAuthModal(true);
                                        return;
                                    }
                                    handleOpenRegistrationModal();
                                }}
                            >
                                {competition.creationType === 'announcement' ? "A NEVEZÉS MÉG ZÁRVA" : "Nevezés leadása"}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* FIX ALSÓ GOMBOK (Lebegő sáv CSAK MOBILON) - KIVÉVE EGYESÜLETEK */}
            {isMobile && !isCheckingUserType && !isClubAccount && (
            <div style={{
                position: 'fixed',
                bottom: isMobile ? (currentUser ? '60px' : '0') : '0',
                left: 0,
                right: 0,
                padding: '16px',
                backgroundColor: 'rgba(255,255,255,0.95)',
                display: 'flex',
                gap: '8px',
                zIndex: 100,
                justifyContent: 'center',
                boxShadow: '0 -4px 10px rgba(0,0,0,0.05)',
                backdropFilter: 'blur(5px)'
            }}>
                <div style={{ maxWidth: '800px', width: '100%', display: 'flex', gap: '8px' }}>
                    <button
                        style={{
                            flex: 1,
                            backgroundColor: checkIsRegistrationClosed(competition)
                                ? '#eeeeee'
                                : (competition.creationType === 'announcement') ? '#ccc' : (isJoined ? (competition.isWithdrawalDisabled ? '#999999' : '#666666') : '#D32F2F'),
                            color: checkIsRegistrationClosed(competition)
                                ? '#000000'
                                : ((competition.creationType === 'announcement' && !isJoined && !isMobile) ? '#000' : '#fff'),
                            border: 'none',
                            borderRadius: '12px',
                            padding: '12px',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                            cursor: (checkIsRegistrationClosed(competition) || competition.creationType === 'announcement' || (isJoined && competition.isWithdrawalDisabled)) ? 'not-allowed' : 'pointer'
                        }}
                        disabled={checkIsRegistrationClosed(competition) || competition.creationType === 'announcement' || (isJoined && competition.isWithdrawalDisabled)}
                        onClick={() => {
                            if (checkIsRegistrationClosed(competition)) return;
                            if (!currentUser) {
                                setShowAuthModal(true);
                                return;
                            }
                            handleOpenRegistrationModal();
                        }}
                    >
                        {checkIsRegistrationClosed(competition)
                            ? "NEVEZÉS LEZÁRULT"
                            : (isJoined ? (competition.isWithdrawalDisabled ? "MÓDOSÍTÁS NEM LEHETSÉGES" : "NEVEZÉS MÓDOSÍTÁSA") : (competition.creationType === 'announcement' ? "A NEVEZÉS MÉG ZÁRVA" : "NEVEZÉS"))
                        }
                    </button>

                    {isJoined && !competition.isWithdrawalDisabled && (
                        <button
                            onClick={handleOpenWithdrawModal}
                            style={{
                                flex: 1,
                                backgroundColor: '#D32F2F',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '12px',
                                padding: '12px',
                                fontWeight: 'bold',
                                fontSize: '1rem',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                cursor: 'pointer'
                            }}
                        >
                            NEVEZÉS LEMONDÁSA
                        </button>
                    )}
                </div>
            </div>
            )}

            {/* --- PÁLYAADATOK MODAL (WEBER) --- */}
            {showTargetsModal && (
                <div style={modalOverlayStyle} onClick={() => setShowTargetsModal(false)}>
                    <div style={{ ...modalContentStyle(isMobile), maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
                        <div style={modalHeaderStyle(isMobile)}>
                            <h3>Pályaadatok (Távolságok)</h3>
                            <div
                                style={{ cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => setShowTargetsModal(false)}
                            >
                                <FiX size={20} color="#fff" />
                            </div>
                        </div>
                        <div style={modalBodyStyle(isMobile)}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #eee' }}>
                                        <th style={{ padding: '10px', textAlign: 'center', width: '40px' }}>#</th>
                                        <th style={{ padding: '10px', textAlign: 'left' }}>Cél</th>
                                        <th style={{ padding: '10px', textAlign: 'center', color: '#b30000' }}>Piros</th>
                                        <th style={{ padding: '10px', textAlign: 'center', color: '#007bff' }}>Kék</th>
                                        <th style={{ padding: '10px', textAlign: 'center', color: '#666' }}>Fehér</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.from({ length: Number(competition.numberOfTargets) || 0 }, (_, i) => i + 1).map(num => (
                                        <tr key={num} style={{ borderBottom: '1px solid #f9f9f9' }}>
                                            <td style={{ padding: '12px 5px', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8f8f8' }}>{num}.</td>
                                            <td style={{ padding: '12px 10px', textAlign: 'left', fontWeight: '500' }}>
                                                {competition.targetDetails?.[num]?.name || '-'}
                                            </td>
                                            <td style={{ padding: '12px 5px', textAlign: 'center', fontWeight: 'bold' }}>
                                                {competition.targetDetails?.[num]?.distRed ? `${competition.targetDetails[num].distRed}m` : '-'}
                                            </td>
                                            <td style={{ padding: '12px 5px', textAlign: 'center', fontWeight: 'bold' }}>
                                                {competition.targetDetails?.[num]?.distBlue ? `${competition.targetDetails[num].distBlue}m` : '-'}
                                            </td>
                                            <td style={{ padding: '12px 5px', textAlign: 'center', fontWeight: 'bold' }}>
                                                {competition.targetDetails?.[num]?.distWhite ? `${competition.targetDetails[num].distWhite}m` : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={modalFooterStyle(isMobile)}>
                            <button style={cancelButtonStyle} onClick={() => setShowTargetsModal(false)}>Bezárás</button>
                        </div>
                    </div>
                </div>
            )}
            {/* --- BEJELENTKEZÉSI FELHÍVÁS MODAL --- */}
            {showAuthModal && (
                <div style={modalOverlayStyle} onClick={() => setShowAuthModal(false)}>
                    <div style={modalContentStyle(isMobile)} onClick={(e) => e.stopPropagation()}>
                        <div style={modalHeaderStyle(isMobile)}>
                            <h3>Bejelentkezés szükséges</h3>
                            <div
                                style={{ cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => setShowAuthModal(false)}
                            >
                                <FiX size={20} color="#fff" />
                            </div>
                        </div>
                        <div style={modalBodyStyle(isMobile)}>
                            <div style={{ color: '#b30000', marginBottom: '15px' }}>
                                <FiUser size={50} />
                            </div>
                            <p style={{ color: '#333', fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '10px' }}>
                                Nevezés leadásához bejelentkezés szükséges!
                            </p>
                            <p style={{ color: '#666', fontSize: '0.9rem', lineHeight: '1.5', marginBottom: '20px' }}>
                                A nevezéshez érvényes felhasználói fiókra van szükséged. Ha már van ArcheryNexus profilod jelentkezz be! Ha még nem regisztráltál pár kattintással megteheted!
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <button
                                    style={{ ...regSubmitButtonStyle, marginTop: 0 }}
                                    onClick={() => {
                                        // MENTJÜK A VISSZATÉRÉSI PONTOT + PARAMÉTER AZ AUTOMATA NYITÁSHOZ
                                        sessionStorage.setItem('redirectAfterLogin', window.location.pathname + '?autoOpen=true');
                                        sessionStorage.setItem('onlyAthlete', 'true');
                                        setShowAuthModal(false);
                                        navigate('/?login=true');
                                    }}
                                >
                                    Bejelentkezés / Regisztráció
                                </button>
                                <button
                                    style={{ ...cancelButtonStyle, marginTop: 0 }}
                                    onClick={() => setShowAuthModal(false)}
                                >
                                    Mégse
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* --- ÚJ: NEVEZÉS KEZELÉSE MODAL --- */}
            {showOptionsModal && (
                <div style={modalOverlayStyle} onClick={() => setShowOptionsModal(false)}>
                    <div style={modalContentStyle(isMobile)} onClick={(e) => e.stopPropagation()}>
                        <div style={modalHeaderStyle(isMobile)}>
                            <h3>Nevezés kezelése</h3>
                            <div
                                style={{ cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => setShowOptionsModal(false)}
                            >
                                <FiX size={20} color="#fff" />
                            </div>
                        </div>
                        <div style={modalBodyStyle(isMobile)}>
                            <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '25px', lineHeight: '1.5' }}>
                                Mit szeretnél tenni a jelenlegi nevezéseddel?
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <button
                                    onClick={() => {
                                        setShowOptionsModal(false);
                                        setShowRegModal(true);
                                    }}
                                    style={{
                                        ...regSubmitButtonStyle,
                                        marginTop: 0,
                                        backgroundColor: '#666666',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '10px'
                                    }}
                                >
                                    <FiEdit2 /> Nevezés szerkesztése
                                </button>

                                <button
                                    onClick={() => {
                                        setShowOptionsModal(false);
                                        handleOpenWithdrawModal();
                                    }}
                                    style={{
                                        ...withdrawButtonStyle,
                                        marginTop: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '10px'
                                    }}
                                >
                                    <FiTrash2 /> Nevezés törlése
                                </button>

                                <button
                                    onClick={() => setShowOptionsModal(false)}
                                    style={{ ...cancelButtonStyle, marginTop: '10px' }}
                                >
                                    Mégse
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- ÚJ: PÁLYATÉRKÉP MODAL (OKOS NÉZET) --- */}
            {showMapModal && (
                <div style={modalOverlayStyle} onClick={() => setShowMapModal(false)}>
                    <div style={{
                        ...modalContentStyle(isMobile),
                        maxWidth: isMobile ? '100%' : '90vw',
                        width: isMobile ? '100%' : 'fit-content',
                        maxHeight: '100vh',
                        height: isMobile ? '100vh' : 'auto',
                        backgroundColor: '#000',
                        borderRadius: isMobile ? '0' : '15px'
                    }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ ...modalHeaderStyle(isMobile), backgroundColor: '#000', borderBottom: '1px solid #333' }}>
                            <h3 style={{ margin: 0, color: '#fff', fontSize: isMobile ? '1rem' : '1.2rem' }}>Pályatérkép</h3>
                            <div
                                style={{ cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => setShowMapModal(false)}
                            >
                                <FiX size={20} color="#fff" />
                            </div>
                        </div>
                        <div style={{
                            flex: 1,
                            backgroundColor: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            position: 'relative',
                            padding: isMobile ? '10px' : '20px'
                        }}>
                            {/* ÚJ: FIX BEZÁRÓ GOMB MOBILON A JOBB FELSŐ SAROKBAN */}
                            {isMobile && (
                                <div
                                    onClick={() => setShowMapModal(false)}
                                    style={{
                                        position: 'absolute', top: '20px', right: '20px',
                                        backgroundColor: '#b30000', color: 'white',
                                        width: '40px', height: '40px', borderRadius: '50%',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: '0 4px 10px rgba(0,0,0,0.5)', zIndex: 100
                                    }}
                                >
                                    <FiX size={24} />
                                </div>
                            )}

                            <img
                                src={mapUrlWithCacheBuster} // GYORSÍTÓTÁR-ROMBOLÓVAL
                                alt="Pályatérkép"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: isMobile ? '80vh' : '85vh',
                                    objectFit: 'contain',
                                    transition: 'all 0.3s ease'
                                }}
                            />
                        </div>
                        {!isMobile && (
                            <div style={{ ...modalFooterStyle(isMobile), backgroundColor: '#111', borderTop: '1px solid #333' }}>
                                <p style={{ color: '#888', fontSize: '0.8rem', margin: 0 }}>
                                    A4 tájolású pályatérkép megtekintése.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FullViewCompetition;



